/**
 * Loading / empty / error / populated state machine for the data table — TanStack
 * ships none, so this module owns all four states and their transitions once.
 * `deriveTableState` is the single mapping from React Query flags to a `TableState`;
 * the client (static-`data`) tier passes no query and is its all-false, rowCount-driven
 * projection. The overlay COMPONENTS that render these states live in `states.tsx`.
 */
import { useEffect, useRef } from 'react'

/**
 * The four table states. LoadingMore / RefetchOverlay / LoadMoreError are NOT
 * separate values — they are Populated with an in-flight sub-signal, because the rows
 * stay MOUNTED under them; modelling them as states would imply an unmount, the
 * stranded-empty bug the `keepPreviousData` window forbids.
 */
export type DataTableStateKind = 'initialLoading' | 'populated' | 'empty' | 'error'

/**
 * The React Query flags the machine maps — each is a field a `useQuery` /
 * `useInfiniteQuery` result already exposes (thread it in, no adapter). All optional:
 * the client tier passes no `query` and is the all-false projection.
 */
export type DataTableQueryState = {
  /**
   * `isPending` — no data yet AND a fetch in flight (first load). Gates the skeleton
   * so no empty flash before the first fetch. False the moment ANY data (incl.
   * placeholder) exists, so a `keepPreviousData` refetch never re-enters `initialLoading`.
   */
  isPending?: boolean
  /** `isError` — the top-level query rejected; drives the error state. */
  isError?: boolean
  /** `isFetching` — a fetch in flight (initial or background); with rows shown, the RefetchOverlay signal. */
  isFetching?: boolean
  /**
   * `keepPreviousData` is serving the previous query's rows during a refetch (v5
   * `isPlaceholderData`) — the strongest "rows are stale" signal; preset gates
   * selection/focus on it.
   */
  placeholderData?: boolean
  /** The top-level query error, kept inspectable by the caller (`no-silent-failures`) — surfaced, never swallowed. */
  error?: unknown
  /** Re-run the top-level query — the error overlay's retry affordance. */
  refetch?: () => void
  /**
   * The next-page error. The machine surfaces a bottom-of-list inline retry instead
   * of the analyze table's silent `console.error` (`no-silent-failures`); stays inspectable.
   */
  loadMoreError?: unknown
  /** Retry the failed next-page fetch. */
  retryLoadMore?: () => void
}

/** The derived state — the single output of the mapping; the booleans ride alongside `populated` without unmounting the rows. */
export type DataTableState = {
  kind: DataTableStateKind
  /**
   * A `keepPreviousData` refetch is in flight over the shown rows — they are stale.
   * Preset gates selection/focus on `!isRefetching` so stale rows never drive
   * "N selected" or the focused cell.
   */
  isRefetching: boolean
  /** A next-page fetch is in flight — the LoadingMore signal. */
  isLoadingMore: boolean
  /**
   * Whether the shown rows must NOT drive selection/focus (the `keepPreviousData`
   * window). Named apart from `isRefetching` so the call site reads as "are these
   * rows safe to derive selection/focus from".
   */
  staleRows: boolean
  /** The next-page error, surfaced as the inline retry — never swallowed. */
  loadMoreError: unknown
  /** The top-level query error, surfaced to the overlay / inline banner + live region. */
  error: unknown
}

export type DeriveTableStateParams = {
  /** The loaded (logical, deduped) row count. */
  rowCount: number
  /** The React Query flags. Omitted for the client tier (static `data`), where the machine is rowCount-driven. */
  query?: DataTableQueryState
  /** `isFetchingNextPage` — the LoadingMore signal. */
  isFetchingNextPage?: boolean
}

/**
 * Map the React Query flags + row count to a `TableState`. Pure; the single owned
 * mapping; the client tier is its all-false projection.
 */
export const deriveTableState = (params: DeriveTableStateParams): DataTableState => {
  const { rowCount, query, isFetchingNextPage = false } = params
  const isPending = query?.isPending ?? false
  const isError = query?.isError ?? false
  const isFetching = query?.isFetching ?? false
  const placeholderData = query?.placeholderData ?? false
  const hasRows = rowCount > 0

  // Background refetch over already-shown rows (placeholder data, or a fetch while
  // rows are present and not the first load): those rows are stale.
  const isRefetching = hasRows && !isPending && (placeholderData || isFetching)

  const base = {
    isRefetching,
    isLoadingMore: isFetchingNextPage,
    staleRows: isRefetching,
    loadMoreError: query?.loadMoreError,
    error: query?.error,
  }

  // Error with no usable rows → error overlay + retry. With rows present (a refetch
  // failed while stale rows show) the error rides alongside `populated` as an inline
  // banner rather than blanking useful rows.
  if (isError && !hasRows) {
    return { ...base, kind: 'error' }
  }

  // First load, no cache → skeleton behind the `isPending` gate (no empty flash).
  if (isPending) {
    return { ...base, kind: 'initialLoading' }
  }

  // Rows → populated (sub-signals carry RefetchOverlay / LoadingMore / LoadMoreError); else empty.
  return { ...base, kind: hasRows ? 'populated' : 'empty' }
}

/**
 * Live-region copy. Optional English defaults; the app overrides with translated
 * strings. `selectionCount` is a function because the count is dynamic.
 */
export type DataTableAnnouncements = {
  /** Initial load resolved to rows. Default "Table loaded". */
  loaded?: (rowCount: number) => string
  /** Initial load resolved to zero rows. Default "No results". */
  empty?: string
  /** (assertive) The query errored. Default "Failed to load data". */
  error?: string
  /** (assertive) A next-page fetch failed. Default "Failed to load more". */
  loadMoreError?: string
  /** (polite) The selection count changed. Default "N selected". */
  selectionCount?: (count: number) => string
  /**
   * (polite) "Select all N matching" is active but the exact count is unknown
   * (unresolved server total). Default "All matching rows selected" — not a
   * misleading "0 selected".
   */
  allMatchingSelected?: string
}

const DEFAULT_ANNOUNCEMENTS = {
  loaded: () => 'Table loaded',
  empty: 'No results',
  error: 'Failed to load data',
  loadMoreError: 'Failed to load more',
  selectionCount: (count: number) => `${count} selected`,
  allMatchingSelected: 'All matching rows selected',
} satisfies Required<DataTableAnnouncements>

export type UseTableAnnouncementsParams = {
  state: DataTableState
  /** Loaded (logical) row count — feeds the "Table loaded" announcement. */
  rowCount: number
  /**
   * Exact selected count, or `undefined` when "select all N matching" can't resolve
   * a number yet (unresolved server total). `undefined` announces the "all matching"
   * string, not a misleading "0 selected" — the degraded count must not reach the live region.
   */
  selectedCountExact: number | undefined
  announcements?: DataTableAnnouncements
}

export type TableAnnouncements = {
  /** The polite message to render in the status region (loaded / selection). */
  polite: string
  /** The assertive message to render in the alert region (error / load-more error). */
  assertive: string
}

/**
 * Derive the live-region messages from state transitions. Edge-triggered on the
 * meaningful transitions (via refs of previous values), not posted every render:
 * re-posting the same text can make some screen readers re-announce or go silent.
 * Each message clears on the next non-triggering render, so a repeat event re-fires
 * (empty→text is what the AT reads).
 */
export const useTableAnnouncements = (params: UseTableAnnouncementsParams): TableAnnouncements => {
  const { state, rowCount, selectedCountExact, announcements } = params
  const loaded = announcements?.loaded ?? DEFAULT_ANNOUNCEMENTS.loaded
  const emptyText = announcements?.empty ?? DEFAULT_ANNOUNCEMENTS.empty
  const errorText = announcements?.error ?? DEFAULT_ANNOUNCEMENTS.error
  const loadMoreErrorText = announcements?.loadMoreError ?? DEFAULT_ANNOUNCEMENTS.loadMoreError
  const selectionCountText = announcements?.selectionCount ?? DEFAULT_ANNOUNCEMENTS.selectionCount
  const allMatchingSelectedText =
    announcements?.allMatchingSelected ?? DEFAULT_ANNOUNCEMENTS.allMatchingSelected

  // Exact count when known, else the "all matching" string — never the degraded 0.
  const selectionMessage =
    selectedCountExact === undefined
      ? allMatchingSelectedText
      : selectionCountText(selectedCountExact)

  const prevKindRef = useRef<DataTableStateKind>(state.kind)
  const prevErrorRef = useRef<boolean>(state.error != null)
  const prevLoadMoreErrorRef = useRef<boolean>(state.loadMoreError != null)
  const prevSelectionMessageRef = useRef<string>(selectionMessage)

  // Emit text only on a genuine transition edge (else ''), so the region announces
  // once per event and an identical repeat re-fires via empty→text. Reading refs in
  // render is a pure "what changed" derivation; the refs only advance in the effect below.
  let polite = ''
  let assertive = ''

  // Leaving the skeleton is a completion cue both ways — an empty first load must
  // hear "no results", not silence, just as a populated load hears "table loaded".
  const leftInitialLoading = prevKindRef.current === 'initialLoading'
  if (leftInitialLoading && state.kind === 'populated') {
    polite = loaded(rowCount)
  } else if (leftInitialLoading && state.kind === 'empty') {
    polite = emptyText
  }

  // A top-level error wins over a same-render load-more error; ordering error-last
  // makes the precedence explicit rather than relying on their mutual exclusion.
  const loadMoreErrorNow = state.loadMoreError != null
  if (loadMoreErrorNow && !prevLoadMoreErrorRef.current) {
    assertive = loadMoreErrorText
  }

  const errorNow = state.error != null
  if (errorNow && !prevErrorRef.current) {
    assertive = errorText
  }

  if (selectionMessage !== prevSelectionMessageRef.current) {
    // Selection is polite (own channel), so it never clobbers a same-render assertive error.
    polite = selectionMessage
  }

  // Advance the "previous" trackers in an effect (not render) so a bailed/retried
  // render doesn't advance them prematurely and swallow an edge.
  useEffect(
    function trackPreviousStateForAnnouncements() {
      prevKindRef.current = state.kind
      prevErrorRef.current = errorNow
      prevLoadMoreErrorRef.current = loadMoreErrorNow
      prevSelectionMessageRef.current = selectionMessage
    },
    [state.kind, errorNow, loadMoreErrorNow, selectionMessage],
  )

  return { polite, assertive }
}
